import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Gift } from 'lucide-react';
import { api, apiError } from '@/lib/api';
import { haptic } from '@/lib/telegram';
import { money } from '@/lib/format';
import { useI18n } from '@/i18n';
import { useAuth } from '@/context/AuthContext';
import { Notice, PageTitle, Spinner } from '@/components/ui';
import { NeedAccount } from './NeedAccount';

function waitText(t: (k: string, v?: Record<string, string | number>) => string, minutes: number): string {
    if (minutes >= 24 * 60) return t('gift.waitDays', { days: Math.ceil(minutes / (24 * 60)) });
    if (minutes >= 60) return t('gift.waitHours', { hours: Math.ceil(minutes / 60) });
    return t('gift.waitMinutes', { minutes });
}

export function GiftCodesPage() {
    const { t, te, lang } = useI18n();
    const { me } = useAuth();
    const qc = useQueryClient();
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState<{ tone: 'ok' | 'warn'; text: string } | null>(null);
    const status = useQuery({
        queryKey: ['gift', 'status'],
        queryFn: async () => (await api.get<{ canRedeem: boolean; minutes?: number; periodDays: number }>('/gift-codes/status')).data,
        enabled: !!me?.registered
    });
    if (!me?.registered) return <NeedAccount />;

    const redeem = async () => {
        setBusy(true); setMsg(null);
        try {
            const { data } = await api.post<{ amount: number; currency: string }>('/gift-codes/redeem', { code });
            setMsg({ tone: 'ok', text: t('gift.success', { amount: money(data.amount, data.currency, lang) }) });
            setCode('');
            haptic('success');
            void qc.invalidateQueries({ queryKey: ['balance'] });
            void status.refetch();
        } catch (e) {
            const err = apiError(e);
            setMsg({ tone: 'warn', text: err.error === 'giftcode_wait' && typeof err.minutes === 'number' ? waitText(t, err.minutes) : te(err) });
            haptic('error');
        } finally { setBusy(false); }
    };
    const blocked = status.data && !status.data.canRedeem;
    return (
        <div className="space-y-4">
            <PageTitle title={t('gift.title')} subtitle={t('gift.subtitle')} />
            <div className="card-lime flex flex-col items-center text-center gap-3 py-6">
                <Gift className="w-10 h-10 text-brand-300" />
                <p className="text-sm text-ink-muted">{t('gift.text')}</p>
            </div>
            {blocked && <Notice tone="warn">{waitText(t, status.data?.minutes || 0)}</Notice>}
            <input className="input text-center text-lg font-bold tracking-widest uppercase" placeholder={t('gift.placeholder')} maxLength={32}
                   value={code} onChange={e => { setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, '')); setMsg(null); }} />
            {msg && <Notice tone={msg.tone}>{msg.text}</Notice>}
            <button className="btn-primary w-full" disabled={busy || code.length < 3 || !!blocked} onClick={redeem}>
                {busy ? <Spinner className="w-4 h-4" /> : <Gift className="w-4 h-4" />} {t('gift.redeem')}
            </button>
        </div>
    );
}
